import { useEffect, useMemo, useRef } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import type { SessionInfo } from '@cm/protocol';
import { useStore } from '../store';
import { decodeOsc52 } from '../osc52';
import { fitFontSize, terminalTheme } from '../theme';
import { NARROW_QUERY } from '../viewport';
import { connection } from '../ws';

/** Dedo que andou mais que isso está rolando o histórico, não pedindo o teclado. */
const TAP_SLOP_PX = 10;

function isNarrow(): boolean {
  return window.matchMedia(NARROW_QUERY).matches;
}

interface Props {
  session: SessionInfo;
  focused: boolean;
  onFocus: () => void;
  /** Cor do pane: tinge o fundo e pinta o cursor. */
  accent: string;
  /** Tamanho fixo escolhido para este pane; sem ele, vale a regra global. */
  fontOverride?: number;
}

/**
 * Um pane = um xterm ligado a uma sessao do daemon.
 *
 * O terminal e criado uma unica vez por sessao e nunca recriado em re-render,
 * senao o scrollback se perderia a cada mudanca de estado.
 */
export function TerminalPane({ session, focused, onFocus, accent, fontOverride }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  /** Gesto de toque em andamento. No celular, pointerdown ainda não sabe se vai rolar. */
  const touchRef = useRef<{ id: number; x: number; y: number; typing: boolean; scrolled: boolean } | null>(null);
  const disposedRef = useRef(false);
  /** O safeFit do efeito de criacao, para os outros efeitos poderem chamar. */
  const refitRef = useRef<(() => void) | null>(null);

  // O tema inteiro do terminal: a paleta do tema ativo mais a tinta do pane.
  // `theme` e uma referencia estavel no store (so muda no setTheme), entao este
  // memo so recalcula quando o tema ou a cor do pane mudam de verdade.
  const theme = useStore((s) => s.theme);
  const termTheme = useMemo(() => terminalTheme(theme, accent), [theme, accent]);

  // Lido dentro do efeito de criacao sem entrar nas deps dele: recriar o
  // Terminal a cada troca de tema apagaria o scrollback, que e justamente o que
  // o efeito abaixo evita.
  const initialThemeRef = useRef(termTheme);
  initialThemeRef.current = termTheme;

  // Mesmo tratamento para a fonte: lida por ref dentro do efeito de criacao,
  // para mudar de tamanho nao recriar o terminal e apagar o scrollback.
  const fonts = useStore((s) => s.fonts);
  const fontStateRef = useRef({ fonts, fontOverride });
  fontStateRef.current = { fonts, fontOverride };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    disposedRef.current = false;

    const term = new Terminal({
      theme: initialThemeRef.current,
      fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: fontStateRef.current.fontOverride ?? fontStateRef.current.fonts.termBase,
      lineHeight: 1.15,
      cursorBlink: true,
      // Os TUIs redesenham a tela inteira; um scrollback grande no cliente
      // pesa sem ajudar, porque o replay do daemon ja cobre a reconexao.
      scrollback: 5_000,
      allowProposedApi: true,
      convertEol: false,
    });

    /**
     * OSC 52: o agente copiando para a area de transferencia DO NAVEGADOR.
     *
     * A decodificacao (e a recusa a consultas) mora em osc52.ts, que e logica
     * pura e tem teste. Aqui fica so a ponte com o navegador.
     */
    term.parser.registerOscHandler(52, (data) => {
      const texto = decodeOsc52(data);
      if (texto === null) return true;

      // `writeText` exige contexto seguro (o cockpit e HTTPS) e costuma exigir
      // gesto recente do usuario -- que existe, porque foi um clique dele no
      // terminal que disparou a copia. Se mesmo assim o navegador recusar, o
      // toast entrega o texto para copiar na mao, em vez de falhar calado.
      void navigator.clipboard?.writeText(texto).catch(() => {
        useStore.getState().pushToast({
          kind: 'warn',
          text: `O navegador recusou a cópia. Texto: ${texto.slice(0, 200)}`,
        });
      });

      return true;
    });

    const fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());
    term.open(host);
    termRef.current = term;
    fitRef.current = fit;

    // Campo somente-leitura não puxa o teclado do celular quando o xterm foca
    // sozinho (o mousedown dele dispara no começo de um toque). Um toque parado
    // tira o somente-leitura e foca de novo, aí o teclado pode abrir.
    const helper = term.textarea;
    if (helper && isNarrow()) helper.readOnly = true;
    const onHelperBlur = () => {
      if (helper && isNarrow()) helper.readOnly = true;
    };
    helper?.addEventListener('blur', onHelperBlur);

    /**
     * `fit()` le dimensoes internas do renderizador do xterm e estoura com
     * "cannot read properties of undefined (reading 'dimensions')" se for
     * chamado antes do primeiro paint, ou depois do dispose. Guardamos os tres
     * casos: componente vivo, host com tamanho e terminal ainda no DOM.
     */
    const safeFit = () => {
      if (disposedRef.current) return;
      const currentHost = hostRef.current;
      if (!currentHost || currentHost.clientWidth < 16 || currentHost.clientHeight < 16) return;
      if (!term.element?.isConnected) return;
      try {
        const dims = fit.proposeDimensions();
        if (!dims) return;

        /**
         * Ajusta a fonte ao espaco ANTES de medir de novo.
         *
         * `proposeDimensions()` ja devolveu quantas colunas cabem com a fonte
         * atual; `fitFontSize` faz a regra de tres para descobrir qual fonte
         * caberia TARGET_COLS. Medir e so entao decidir evita depender da
         * largura da celula, que muda com DPI e zoom do navegador.
         *
         * O passo minimo de meio pixel e o que impede oscilacao: sem ele, uma
         * diferenca de arredondamento faria fonte e colunas se empurrarem a
         * cada ResizeObserver, num laco visivel de tremor.
         */
        const { fonts: f, fontOverride: ov } = fontStateRef.current;
        const atual = term.options.fontSize ?? f.termBase;
        const alvo = fitFontSize(f, atual, dims.cols, ov);
        if (Math.abs(alvo - atual) >= 0.5) {
          term.options.fontSize = alvo;
          // A medida anterior valia para a fonte antiga; remede com a nova.
          const redims = fit.proposeDimensions();
          if (redims) {
            dims.cols = redims.cols;
            dims.rows = redims.rows;
          }
        }

        const { cols, rows } = dims;
        if (!Number.isFinite(cols) || !Number.isFinite(rows) || cols < 2 || rows < 2) return;
        if (cols !== term.cols || rows !== term.rows) term.resize(cols, rows);
        connection.resize(session.id, term.cols, term.rows);
      } catch {
        /* renderizador ainda nao esta pronto; a proxima tentativa resolve */
      }
    };

    refitRef.current = safeFit;

    // Tenta algumas vezes: o primeiro paint pode nao ter acontecido ainda, e
    // um pane recem-criado entra na grade sem tamanho definitivo. Parar no
    // primeiro tamanho > 2 linhas publica um TUI curto (o celular mede ~11
    // linhas antes da coluna flex assentar) e o agente redesenha torto.
    let attempts = 0;
    let lastH = -1;
    const warmup = window.setInterval(() => {
      attempts += 1;
      safeFit();
      const hostH = hostRef.current?.clientHeight ?? 0;
      const settled = hostH >= 16 && Math.abs(hostH - lastH) < 1 && term.rows > 2;
      lastH = hostH;
      if (attempts >= 12 || (settled && attempts >= 3)) window.clearInterval(warmup);
    }, 80);

    const offData = connection.subscribe(session.id, (data, isReplay) => {
      // O replay e o estado completo do scrollback: limpar antes evita que a
      // tela apareca duplicada em cada reconexao.
      if (isReplay) term.reset();
      term.write(data);
    });

    const inputHandler = term.onData((data) => connection.sendInput(session.id, data));

    // Resize em rAF: o ResizeObserver dispara em rajada durante o arraste da
    // janela, e remedir a cada evento trava o terminal.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        safeFit();
      });
    });
    observer.observe(host);

    return () => {
      disposedRef.current = true;
      window.clearInterval(warmup);
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      helper?.removeEventListener('blur', onHelperBlur);
      inputHandler.dispose();
      offData();
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
      refitRef.current = null;
    };
    // Trocar de sessao significa outro pane; o id e a unica dependencia real.
  }, [session.id]);

  useEffect(() => {
    // No celular o foco automático abre o teclado ao trocar de agente ou ao
    // montar o pane. Lá o teclado só entra num toque parado.
    if (focused && !isNarrow()) termRef.current?.focus();
  }, [focused]);

  /**
   * Troca de tema sem recriar o Terminal.
   *
   * Efeito separado, com deps proprias: o de cima depende so de `session.id`
   * porque recriar o terminal perde o scrollback. O xterm aceita reatribuir
   * `options.theme` a quente.
   *
   * O `refresh` nao e enfeite -- sem ele as linhas ja desenhadas so trocam de
   * cor no proximo write do agente, e um TUI parado ficaria com a paleta
   * antiga na tela por tempo indefinido.
   */
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;
    term.options.theme = termTheme;
    term.refresh(0, term.rows - 1);
  }, [termTheme]);

  /**
   * Reage a mudanca de tamanho de fonte (zoom global ou override do pane).
   *
   * Nao mexe no fontSize aqui: so dispara um refit. Quem decide o tamanho e o
   * `safeFit`, que e o unico ponto com a medida do xterm em maos -- ter duas
   * regras de tamanho em lugares diferentes e como se perde a convergencia.
   */
  useEffect(() => {
    refitRef.current?.();
  }, [fonts.termBase, fonts.termAuto, fonts.termMin, fontOverride]);

  return (
    <div
      className="pane-term"
      ref={hostRef}
      onPointerDown={(event) => {
        if (event.pointerType === 'mouse') {
          const helper = termRef.current?.textarea;
          if (helper) helper.readOnly = false;
          onFocus();
          termRef.current?.focus();
          return;
        }
        const helper = termRef.current?.textarea;
        const typing = !!helper && document.activeElement === helper && !helper.readOnly;
        if (helper && !typing) helper.readOnly = true;
        touchRef.current = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          typing,
          scrolled: false,
        };
      }}
      onPointerMove={(event) => {
        const start = touchRef.current;
        if (!start || start.id !== event.pointerId || start.typing || start.scrolled) return;
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) <= TAP_SLOP_PX) return;
        start.scrolled = true;
        const helper = termRef.current?.textarea;
        if (helper && document.activeElement === helper) helper.blur();
      }}
      onPointerUp={(event) => {
        const start = touchRef.current;
        touchRef.current = null;
        if (!start || start.id !== event.pointerId || event.pointerType === 'mouse') return;
        const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
        if (start.scrolled || start.typing || moved > TAP_SLOP_PX) {
          if (!start.typing && (start.scrolled || moved > TAP_SLOP_PX)) {
            const field = termRef.current?.textarea;
            if (field && document.activeElement === field) field.blur();
          }
          return;
        }
        // Tem de ser neste pointerup. Focar depois do render chega tarde e o
        // Safari do iPhone não abre o teclado.
        const helper = termRef.current?.textarea;
        if (helper) helper.readOnly = false;
        onFocus();
        termRef.current?.focus();
      }}
      onPointerCancel={() => {
        touchRef.current = null;
      }}
    />
  );
}
